import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import TableRowsIcon from '@mui/icons-material/TableRows';
import PageHeader from '../components/common/PageHeader';
import DashboardSkeleton from '../components/common/DashboardSkeleton';
import UrlaubKalender from '../components/urlaub/UrlaubKalender';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import { urlaubService } from '../services/urlaubService';
import { krankheitService } from '../services/krankheitService';
import { workationService } from '../services/workationService';
import {
  MitarbeiterUrlaubUebersicht,
  UrlaubUebersichtResponse,
  Urlaub,
  Krankheit,
  Workation,
} from '../types';

/* ---- Helper ---- */

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getInitials = (vorname: string, nachname: string) =>
  `${vorname.charAt(0)}${nachname.charAt(0)}`.toUpperCase();

const getAvatarColor = (name: string) => {
  const colors = [
    '#3dd47e', '#42a5f5', '#ffa726', '#ce93d8', '#ef5350',
    '#26c6da', '#ff7043', '#66bb6a', '#ab47bc', '#29b6f6',
    '#8d6e63', '#78909c',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

/* ---- Status Chip ---- */

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'Genehmigt':
      return <Chip label="Genehmigt" size="small" sx={{ bgcolor: '#1b5e20', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />;
    case 'Abgelehnt':
      return <Chip label="Abgelehnt" size="small" sx={{ bgcolor: '#b71c1c', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />;
    case 'Beantragt':
    default:
      return <Chip label="Beantragt" size="small" sx={{ bgcolor: '#e65100', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />;
  }
};

/* ---- Vacation Bar (visual) ---- */

interface VacationBarProps {
  gesamt: number;
  genehmigt: number;
  beantragt: number;
  isDark: boolean;
}

const VacationBar: React.FC<VacationBarProps> = ({ gesamt, genehmigt, beantragt, isDark }) => {
  const genehmigtPct = gesamt > 0 ? Math.min((genehmigt / gesamt) * 100, 100) : 0;
  const beantraktPct = gesamt > 0 ? Math.min((beantragt / gesamt) * 100, 100 - genehmigtPct) : 0;
  const verbleibendPct = Math.max(100 - genehmigtPct - beantraktPct, 0);

  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: '0.75rem' }}>
          <Box>Genehmigt: {genehmigt} Tage ({genehmigtPct.toFixed(0)}%)</Box>
          <Box>Beantragt: {beantragt} Tage ({beantraktPct.toFixed(0)}%)</Box>
          <Box>Verbleibend: {gesamt - genehmigt} Tage ({verbleibendPct.toFixed(0)}%)</Box>
        </Box>
      }
      arrow
    >
      <Box sx={{ width: '100%', minWidth: 120 }}>
        <Box
          sx={{
            display: 'flex',
            height: 10,
            borderRadius: 5,
            overflow: 'hidden',
            bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          {genehmigtPct > 0 && (
            <Box
              sx={{
                width: `${genehmigtPct}%`,
                bgcolor: '#4caf50',
                transition: 'width 0.5s ease',
              }}
            />
          )}
          {beantraktPct > 0 && (
            <Box
              sx={{
                width: `${beantraktPct}%`,
                bgcolor: '#ff9800',
                transition: 'width 0.5s ease',
              }}
            />
          )}
          {verbleibendPct > 0 && (
            <Box
              sx={{
                width: `${verbleibendPct}%`,
                bgcolor: 'transparent',
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
            {genehmigt + beantragt}/{gesamt}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
            {gesamt - genehmigt} frei
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

/* ---- Mitarbeiter Row (expandable) ---- */

interface MitarbeiterRowProps {
  ma: MitarbeiterUrlaubUebersicht;
  isDark: boolean;
  canApprove: boolean;
  currentUserId: number | undefined;
  highlightUrlaubId: number | null;
  onApprove: (u: Urlaub) => void;
  onReject: (u: Urlaub) => void;
}

const MitarbeiterRow: React.FC<MitarbeiterRowProps> = ({
  ma,
  isDark,
  canApprove,
  currentUserId,
  highlightUrlaubId,
  onApprove,
  onReject,
}) => {
  const hasHighlight =
    highlightUrlaubId != null && ma.antraege.some((a) => a.id === highlightUrlaubId);
  const [open, setOpen] = useState(hasHighlight);

  useEffect(() => {
    if (hasHighlight) setOpen(true);
  }, [hasHighlight]);

  useLayoutEffect(() => {
    if (!highlightUrlaubId || !hasHighlight || !open) return;
    const t = window.setTimeout(() => {
      document
        .getElementById(`urlaub-ant-highlight-${highlightUrlaubId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 280);
    return () => window.clearTimeout(t);
  }, [highlightUrlaubId, hasHighlight, open]);
  const fullName = `${ma.vorname} ${ma.nachname}`;
  const avatarColor = getAvatarColor(fullName);
  const pendingCount = ma.antraege.filter((a) => a.status === 'Beantragt').length;

  const verbleibendColor =
    ma.verbleibend <= 0 ? '#ef5350' : ma.verbleibend <= 5 ? '#ff9800' : '#4caf50';

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: ma.antraege.length > 0 ? 'pointer' : 'default',
          '& > *': { borderBottom: open ? 'none' : undefined },
        }}
        onClick={() => ma.antraege.length > 0 && setOpen(!open)}
      >
        <TableCell sx={{ width: 40, pr: 0 }}>
          {ma.antraege.length > 0 && (
            <IconButton size="small" sx={{ color: 'text.secondary' }}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: '0.8rem',
                fontWeight: 700,
                bgcolor: avatarColor,
                color: '#fff',
              }}
            >
              {getInitials(ma.vorname, ma.nachname)}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {fullName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {ma.email}
              </Typography>
            </Box>
            {pendingCount > 0 && (
              <Chip
                label={`${pendingCount} offen`}
                size="small"
                sx={{
                  bgcolor: '#e65100',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                  ml: 0.5,
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell align="center">
          <Tooltip
            title={
              ma.jahresanspruch_anteilig && ma.anteilige_monate != null
                ? `${ma.jahresanspruch} Jahresanspruch (${ma.anteilige_monate}/12 × ${ma.urlaubstage_gesamt} Tage)${ma.urlaub_uebertrag_auto > 0 ? ` + ${ma.urlaub_uebertrag_auto} Auto` : ''}${ma.urlaub_uebertrag_manuell > 0 ? ` + ${ma.urlaub_uebertrag_manuell} manuell` : ''}${ma.uebertrag_hinweis_frist_ueberschritten && ma.urlaub_uebertrag_auto > 0 ? ' · Hinweisfrist überschritten' : ma.uebertrag_hinweis_frist_am && ma.urlaub_uebertrag_auto > 0 ? ` · Planungshinweis bis ${formatDate(ma.uebertrag_hinweis_frist_am)}` : ''}`
                : ma.urlaub_uebertrag > 0 || ma.jahresanspruch !== ma.anspruch_gesamt
                ? `${ma.jahresanspruch} Jahresanspruch${ma.urlaub_uebertrag_auto > 0 ? ` + ${ma.urlaub_uebertrag_auto} Auto` : ''}${ma.urlaub_uebertrag_manuell > 0 ? ` + ${ma.urlaub_uebertrag_manuell} manuell` : ''} (Stamm: ${ma.urlaubstage_gesamt}/Jahr)${ma.uebertrag_hinweis_frist_ueberschritten && ma.urlaub_uebertrag_auto > 0 ? ' · Hinweisfrist überschritten' : ma.uebertrag_hinweis_frist_am && ma.urlaub_uebertrag_auto > 0 ? ` · Planungshinweis bis ${formatDate(ma.uebertrag_hinweis_frist_am)}` : ''}`
                : `${ma.urlaubstage_gesamt} Tage/Jahr`
            }
            arrow
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {ma.anspruch_gesamt}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
            {ma.genehmigt}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800' }}>
            {ma.beantragt}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" sx={{ fontWeight: 700, color: verbleibendColor }}>
            {ma.verbleibend}
          </Typography>
        </TableCell>
        <TableCell sx={{ minWidth: 160 }}>
          <VacationBar
            gesamt={ma.anspruch_gesamt}
            genehmigt={ma.genehmigt}
            beantragt={ma.beantragt}
            isDark={isDark}
          />
        </TableCell>
      </TableRow>

      {/* Expanded detail rows */}
      <TableRow>
        <TableCell sx={{ py: 0, border: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                py: 1.5,
                px: 2,
                ml: 5,
                mb: 1,
                borderRadius: 2,
                bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                Urlaubsanträge
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Von</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Bis</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Tage</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Bemerkung</TableCell>
                    {canApprove && (
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="right">
                        Aktionen
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ma.antraege.map((a) => (
                    <TableRow
                      key={a.id}
                      hover
                      id={highlightUrlaubId === a.id ? `urlaub-ant-highlight-${a.id}` : undefined}
                      sx={
                        highlightUrlaubId === a.id
                          ? {
                              bgcolor: isDark ? 'rgba(43, 168, 98, 0.14)' : 'rgba(43, 168, 98, 0.1)',
                              boxShadow: (t) => `inset 0 0 0 2px ${t.palette.success.main}`,
                            }
                          : undefined
                      }
                    >
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(a.von)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(a.bis)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{a.anzahl_tage}</TableCell>
                      <TableCell><StatusChip status={a.status} /></TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.bemerkung || '-'}
                      </TableCell>
                      {canApprove && (
                        <TableCell align="right">
                          {a.status === 'Beantragt' && ma.mitarbeiter_id !== currentUserId && (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Tooltip title="Genehmigen">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#4caf50' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onApprove(a);
                                  }}
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Ablehnen">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#ef5350' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onReject(a);
                                  }}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/* ==== Main Page ==== */

const UrlaubUebersichtPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission(P.URLAUB_APPROVE);
  const canWorkationApprove = hasPermission(P.WORKATION_APPROVE);
  const canKrankheitViewAll = hasPermission(P.KRANKHEIT_VIEW_ALL);
  const canWorkationViewAll = hasPermission(P.WORKATION_VIEW_ALL);
  const [searchParams, setSearchParams] = useSearchParams();

  const offenOnly = useMemo(() => {
    const v = searchParams.get('offen')?.toLowerCase();
    return v === '1' || v === 'true' || v === 'yes';
  }, [searchParams]);

  const focusUrlaubId = useMemo(() => {
    const raw = searchParams.get('urlaub');
    if (raw == null || raw === '') return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const cardBg = isDark ? '#1e2240' : '#ffffff';
  const cardBorder = isDark ? '#2c3358' : '#e0e0e0';

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UrlaubUebersichtResponse | null>(null);
  const [krankheiten, setKrankheiten] = useState<Krankheit[]>([]);
  const [workations, setWorkations] = useState<Workation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'tabelle' | 'kalender'>(
    searchParams.get('tab') === 'kalender' ? 'kalender' : 'tabelle'
  );

  const handleTabChange = (_e: React.SyntheticEvent, value: 'tabelle' | 'kalender') => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === 'kalender') next.set('tab', 'kalender');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  // Admin action dialog
  const [adminDialog, setAdminDialog] = useState<{
    open: boolean;
    action: 'genehmigen' | 'ablehnen';
    typ: 'urlaub' | 'workation';
    urlaub: Urlaub | null;
    workation: Workation | null;
  }>({ open: false, action: 'genehmigen', typ: 'urlaub', urlaub: null, workation: null });
  const [adminBemerkung, setAdminBemerkung] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await urlaubService.uebersicht(selectedYear);
      setData(result);
      if (canKrankheitViewAll) {
        setKrankheiten(await krankheitService.listAlle(selectedYear));
      } else {
        setKrankheiten([]);
      }
      if (canWorkationViewAll) {
        setWorkations(await workationService.listAlle(selectedYear));
      } else {
        setWorkations([]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Laden der Daten';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, canKrankheitViewAll, canWorkationViewAll]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /** Jahr aus Deep-Link (?jahr=), z. B. E-Mail an Genehmiger */
  useEffect(() => {
    const raw = searchParams.get('jahr');
    if (raw == null || raw === '') return;
    const y = Number.parseInt(raw, 10);
    if (Number.isFinite(y) && y >= 1970 && y <= 2100) {
      setSelectedYear(y);
    }
  }, [searchParams]);

  const handleAdminAction = async () => {
    try {
      if (adminDialog.typ === 'urlaub' && adminDialog.urlaub) {
        if (adminDialog.action === 'genehmigen') {
          await urlaubService.genehmigen(adminDialog.urlaub.id, adminBemerkung);
        } else {
          await urlaubService.ablehnen(adminDialog.urlaub.id, adminBemerkung);
        }
      } else if (adminDialog.typ === 'workation' && adminDialog.workation) {
        if (adminDialog.action === 'genehmigen') {
          await workationService.genehmigen(adminDialog.workation.id, adminBemerkung);
        } else {
          await workationService.ablehnen(adminDialog.workation.id, adminBemerkung);
        }
      } else {
        return;
      }
      setAdminDialog({ open: false, action: 'genehmigen', typ: 'urlaub', urlaub: null, workation: null });
      setAdminBemerkung('');
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr?.response?.data?.detail || 'Fehler bei der Aktion');
    }
  };

  // Filtered data (Suche, optional nur offene Anträge aus Deep-Link)
  const filteredMitarbeiter = useMemo(() => {
    let list =
      data?.mitarbeiter_uebersichten.filter((ma) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
          ma.vorname.toLowerCase().includes(search) ||
          ma.nachname.toLowerCase().includes(search) ||
          ma.email.toLowerCase().includes(search)
        );
      }) ?? [];
    if (offenOnly) {
      list = list.filter((ma) => ma.antraege.some((a) => a.status === 'Beantragt'));
    }
    return list;
  }, [data, searchTerm, offenOnly]);

  // Kalender-Items: Urlaub + typisierte Krankheit/Workation (privilegierte Übersicht)
  const kalenderItems = useMemo(() => {
    const urlaubItems = (data?.mitarbeiter_uebersichten ?? []).flatMap((ma) =>
      ma.antraege.map((a) => ({
        id: `u-${a.id}`,
        label: `${ma.vorname} ${ma.nachname} (Urlaub)`,
        von: a.von,
        bis: a.bis,
        status: a.status,
        anzahl_tage: a.anzahl_tage,
        bemerkung: a.bemerkung,
      }))
    );
    const krankItems = krankheiten.map((k) => ({
      id: `k-${k.id}`,
      label: `${k.mitarbeiter ? `${k.mitarbeiter.vorname} ${k.mitarbeiter.nachname}` : `ID ${k.mitarbeiter_id}`} (Krankheit)`,
      von: k.von,
      bis: k.bis,
      status: k.status === 'Gemeldet' ? 'Genehmigt' : k.status,
      anzahl_tage: k.anzahl_tage,
      bemerkung: k.bemerkung,
    }));
    const workItems = workations.map((w) => ({
      id: `w-${w.id}`,
      label: `${w.mitarbeiter ? `${w.mitarbeiter.vorname} ${w.mitarbeiter.nachname}` : `ID ${w.mitarbeiter_id}`} (Workation)`,
      von: w.von,
      bis: w.bis,
      status: w.status,
      anzahl_tage: w.anzahl_tage,
      bemerkung: w.bemerkung,
    }));
    return [...urlaubItems, ...krankItems, ...workItems];
  }, [data, krankheiten, workations]);

  // Summary stats
  const totalUrlaubstage = filteredMitarbeiter.reduce((sum, ma) => sum + ma.anspruch_gesamt, 0);
  const totalGenutzt = filteredMitarbeiter.reduce((sum, ma) => sum + ma.genehmigt, 0);
  const auslastungPct = totalUrlaubstage > 0 ? (totalGenutzt / totalUrlaubstage) * 100 : 0;

  const summaryCards = data
    ? [
        {
          label: 'Mitarbeiter',
          value: data.gesamt_mitarbeiter,
          icon: <PeopleIcon sx={{ fontSize: 30 }} />,
          color: accent,
          subtitle: 'Aktive Mitarbeiter',
        },
        {
          label: 'Offene Anträge',
          value: data.offene_antraege,
          icon: <HourglassEmptyIcon sx={{ fontSize: 30 }} />,
          color: '#ff9800',
          subtitle: 'Warten auf Genehmigung',
        },
        {
          label: 'Genehmigte Tage',
          value: data.genehmigte_tage_gesamt,
          icon: <FlightTakeoffIcon sx={{ fontSize: 30 }} />,
          color: '#4caf50',
          subtitle: `von ${totalUrlaubstage} Tagen gesamt`,
        },
        {
          label: 'Auslastung',
          value: `${auslastungPct.toFixed(0)}%`,
          icon: <TrendingUpIcon sx={{ fontSize: 30 }} />,
          color: auslastungPct > 75 ? '#ef5350' : auslastungPct > 50 ? '#ff9800' : '#42a5f5',
          subtitle: 'Urlaubstage verbraucht',
          isPercentage: true,
        },
      ]
    : [];

  if (loading) {
    return (
      <Box>
        <PageHeader title="Abwesenheitsübersicht" subtitle="Management-Übersicht wird geladen..." breadcrumbs={[{ label: 'Abwesenheitsübersicht' }]} />
        <DashboardSkeleton />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Abwesenheitsübersicht"
        subtitle={`Management-Übersicht ${selectedYear}`}
        breadcrumbs={[{ label: 'Abwesenheitsübersicht' }]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {(offenOnly || focusUrlaubId != null) && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('offen');
                next.delete('urlaub');
                next.delete('jahr');
                setSearchParams(next, { replace: true });
              }}
            >
              Filter zurücksetzen
            </Button>
          }
        >
          {focusUrlaubId != null && 'Der verlinkte Antrag ist in der Tabelle hervorgehoben. '}
          {offenOnly && 'Es werden nur Mitarbeiter mit mindestens einem offenen Antrag angezeigt.'}
        </Alert>
      )}

      {/* View tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 2, borderBottom: `1px solid ${cardBorder}`, minHeight: 42 }}
      >
        <Tab
          value="tabelle"
          icon={<TableRowsIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Tabelle"
          sx={{ minHeight: 42, textTransform: 'none', fontWeight: 600 }}
        />
        <Tab
          value="kalender"
          icon={<CalendarViewMonthIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Kalender"
          sx={{ minHeight: 42, textTransform: 'none', fontWeight: 600 }}
        />
      </Tabs>

      {/* Year selector + Search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
            <Chip
              key={year}
              label={year}
              icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
              variant={selectedYear === year ? 'filled' : 'outlined'}
              onClick={() => setSelectedYear(year)}
              sx={{
                fontWeight: 600,
                ...(selectedYear === year
                  ? { bgcolor: accent, color: isDark ? '#181c34' : '#fff' }
                  : { borderColor: cardBorder }),
              }}
            />
          ))}
        </Box>
        <Box sx={{ flex: 1 }} />
        {activeTab === 'tabelle' && (
          <TextField
            size="small"
            placeholder="Mitarbeiter suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        )}
      </Box>

      {/* Summary Cards */}
      {data && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          {summaryCards.map((card) => (
            <Card
              key={card.label}
              sx={{
                bgcolor: cardBg,
                border: `1px solid ${cardBorder}`,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: card.color,
                },
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${card.color}15`,
                    color: card.color,
                  }}
                >
                  {card.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  >
                    {card.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: card.color, lineHeight: 1.2 }}>
                    {card.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                    {card.subtitle}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Overall progress bar */}
      {data && (
        <Card sx={{ bgcolor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventAvailableIcon sx={{ fontSize: 20, color: accent }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Gesamtauslastung Urlaubstage {selectedYear}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#4caf50' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Genehmigt ({data.genehmigte_tage_gesamt} Tage)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff9800' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Beantragt ({data.beantragte_tage_gesamt} Tage)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Verfügbar ({totalUrlaubstage - data.genehmigte_tage_gesamt} Tage)
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <LinearProgress
                variant="determinate"
                value={totalUrlaubstage > 0 ? Math.min((data.genehmigte_tage_gesamt / totalUrlaubstage) * 100, 100) : 0}
                sx={{
                  height: 14,
                  borderRadius: 7,
                  bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 7,
                    bgcolor: '#4caf50',
                  },
                }}
              />
              {/* Beantragt overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: totalUrlaubstage > 0 ? `${(data.genehmigte_tage_gesamt / totalUrlaubstage) * 100}%` : 0,
                  width: totalUrlaubstage > 0 ? `${Math.min((data.beantragte_tage_gesamt / totalUrlaubstage) * 100, 100 - (data.genehmigte_tage_gesamt / totalUrlaubstage) * 100)}%` : 0,
                  height: 14,
                  bgcolor: '#ff9800',
                  borderRadius: data.genehmigte_tage_gesamt === 0 ? '7px 0 0 7px' : 0,
                  borderTopRightRadius: 7,
                  borderBottomRightRadius: 7,
                  transition: 'width 0.5s ease',
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Kalenderansicht */}
      {activeTab === 'kalender' && (
        <UrlaubKalender items={kalenderItems} selectedYear={selectedYear} isDark={isDark} />
      )}

      {/* Mitarbeiter Table */}
      {activeTab === 'tabelle' && (
      <Card sx={{ bgcolor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 3 }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${cardBorder}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BeachAccessIcon sx={{ color: accent, fontSize: 22 }} />
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            Mitarbeiter-Urlaubsübersicht
          </Typography>
          <Chip
            label={`${filteredMitarbeiter.length} Mitarbeiter`}
            size="small"
            sx={{ ml: 1, fontWeight: 600, fontSize: '0.7rem' }}
          />
        </Box>
        <TableContainer>
          {filteredMitarbeiter.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchTerm
                  ? 'Keine Mitarbeiter gefunden'
                  : offenOnly
                    ? 'Keine offenen Anträge für dieses Jahr'
                    : 'Keine Daten vorhanden'}
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Mitarbeiter</TableCell>
                  <TableCell align="center">Anspruch</TableCell>
                  <TableCell align="center">Genehmigt</TableCell>
                  <TableCell align="center">Beantragt</TableCell>
                  <TableCell align="center">Verbleibend</TableCell>
                  <TableCell>Auslastung</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMitarbeiter.map((ma) => (
                  <MitarbeiterRow
                    key={ma.mitarbeiter_id}
                    ma={ma}
                    isDark={isDark}
                    canApprove={canApprove}
                    currentUserId={user?.id}
                    highlightUrlaubId={focusUrlaubId}
                    onApprove={(u) =>
                      setAdminDialog({
                        open: true,
                        action: 'genehmigen',
                        typ: 'urlaub',
                        urlaub: u,
                        workation: null,
                      })
                    }
                    onReject={(u) =>
                      setAdminDialog({
                        open: true,
                        action: 'ablehnen',
                        typ: 'urlaub',
                        urlaub: u,
                        workation: null,
                      })
                    }
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Card>
      )}

      {canWorkationViewAll && workations.filter((w) => w.status === 'Beantragt').length > 0 && (
        <Card sx={{ mt: 3, bgcolor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Offene Workation-Anträge
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mitarbeiter</TableCell>
                  <TableCell>Von</TableCell>
                  <TableCell>Bis</TableCell>
                  <TableCell>Tage</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workations
                  .filter((w) => w.status === 'Beantragt')
                  .map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>
                        {w.mitarbeiter
                          ? `${w.mitarbeiter.vorname} ${w.mitarbeiter.nachname}`
                          : `ID ${w.mitarbeiter_id}`}
                      </TableCell>
                      <TableCell>{formatDate(w.von)}</TableCell>
                      <TableCell>{formatDate(w.bis)}</TableCell>
                      <TableCell>{w.anzahl_tage}</TableCell>
                      <TableCell align="right">
                        {canWorkationApprove && w.mitarbeiter_id !== user?.id && (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              sx={{ color: '#1b5e20' }}
                              onClick={() =>
                                setAdminDialog({
                                  open: true,
                                  action: 'genehmigen',
                                  typ: 'workation',
                                  urlaub: null,
                                  workation: w,
                                })
                              }
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: '#b71c1c' }}
                              onClick={() =>
                                setAdminDialog({
                                  open: true,
                                  action: 'ablehnen',
                                  typ: 'workation',
                                  urlaub: null,
                                  workation: w,
                                })
                              }
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      {canKrankheitViewAll && krankheiten.length > 0 && (
        <Card sx={{ mt: 3, bgcolor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Krankmeldungen {selectedYear}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mitarbeiter</TableCell>
                  <TableCell>Von</TableCell>
                  <TableCell>Bis</TableCell>
                  <TableCell>Tage</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {krankheiten.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell>
                      {k.mitarbeiter
                        ? `${k.mitarbeiter.vorname} ${k.mitarbeiter.nachname}`
                        : `ID ${k.mitarbeiter_id}`}
                    </TableCell>
                    <TableCell>{formatDate(k.von)}</TableCell>
                    <TableCell>{formatDate(k.bis)}</TableCell>
                    <TableCell>{k.anzahl_tage}</TableCell>
                    <TableCell>
                      <Chip size="small" label={k.status} color="success" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      {/* Admin Action Dialog */}
      <Dialog
        open={adminDialog.open}
        onClose={() => {
          setAdminDialog({ open: false, action: 'genehmigen', typ: 'urlaub', urlaub: null, workation: null });
          setAdminBemerkung('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {adminDialog.typ === 'workation'
            ? adminDialog.action === 'genehmigen'
              ? 'Workation genehmigen'
              : 'Workation ablehnen'
            : adminDialog.action === 'genehmigen'
              ? 'Urlaub genehmigen'
              : 'Urlaub ablehnen'}
        </DialogTitle>
        <DialogContent>
          {(adminDialog.urlaub || adminDialog.workation) && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Mitarbeiter:</strong>{' '}
                {(() => {
                  const ma = adminDialog.urlaub?.mitarbeiter || adminDialog.workation?.mitarbeiter;
                  return ma ? `${ma.vorname} ${ma.nachname}` : '-';
                })()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Zeitraum:</strong>{' '}
                {formatDate((adminDialog.urlaub || adminDialog.workation)!.von)} -{' '}
                {formatDate((adminDialog.urlaub || adminDialog.workation)!.bis)}{' '}
                ({(adminDialog.urlaub || adminDialog.workation)!.anzahl_tage} Tage)
              </Typography>
              {(adminDialog.urlaub?.bemerkung || adminDialog.workation?.bemerkung) && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Bemerkung:</strong>{' '}
                  {adminDialog.urlaub?.bemerkung || adminDialog.workation?.bemerkung}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            label="Admin-Bemerkung (optional)"
            value={adminBemerkung}
            onChange={(e) => setAdminBemerkung(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setAdminDialog({ open: false, action: 'genehmigen', typ: 'urlaub', urlaub: null, workation: null });
              setAdminBemerkung('');
            }}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color={adminDialog.action === 'genehmigen' ? 'success' : 'error'}
            onClick={handleAdminAction}
          >
            {adminDialog.action === 'genehmigen' ? 'Genehmigen' : 'Ablehnen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UrlaubUebersichtPage;
